import type { CampsiteDetails, PitchType } from './types'
import type { ChargeType } from './types'

export const DEFAULT_CHECKIN_TIME = '15:00:00'
export const DEFAULT_CHECKOUT_TIME = '10:00:00'

export interface HotelPriceSchemaInput {
  campsite: CampsiteDetails
  streetAddress: string
  isDated: boolean
  chargeTypesUpdating: boolean
  dateRange: { start: string; end: string }
  chargetypes: ChargeType[]
  pitchtypeId?: string
}

export function buildHotelPriceSchema(
  input: HotelPriceSchemaInput,
): Record<string, unknown> | null {
  const cs = input.campsite
  if (!cs) return null

  const address = {
    '@type': 'PostalAddress',
    streetAddress: input.streetAddress,
    addressLocality: (cs.hierarchy && cs.hierarchy.town) || '',
    addressRegion: cs.countyOrRegion || '',
    postalCode: cs.postcode,
    addressCountry: cs.countryIso || '',
  }

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Hotel',
    name: cs.name,
    identifier: cs.id,
    address,
  }

  if (
    !input.isDated ||
    input.chargeTypesUpdating ||
    !input.dateRange.start ||
    !input.dateRange.end
  )
    return schema

  // Build a pitchtype lookup for names and per-pitchtype times
  const pitchtypeMap = new Map<string, PitchType>(
    (cs.pitchtypes || []).map((pt) => [String(pt.id), pt]),
  )

  // Campsite-level fallback times
  const fallbackCheckin =
    cs.onsiteArrivalTimeFromIso ??
    cs.campingArrivalTimeFromIso ??
    DEFAULT_CHECKIN_TIME
  const fallbackCheckout =
    cs.onsiteDepartTimeFromIso ??
    cs.campingDepartTimeFromIso ??
    DEFAULT_CHECKOUT_TIME

  // Filter charge types: on PTD pages show only that pitchtype, on CSD show all
  const relevantChargeTypes = input.pitchtypeId
    ? input.chargetypes.filter(
        (ct) => String(ct.pitchtypeId) === String(input.pitchtypeId),
      )
    : input.chargetypes

  const availableChargeTypes = relevantChargeTypes.filter(
    (ct) => ct.isAvailable && ct.price,
  )
  if (!availableChargeTypes.length) return schema

  // Build an offer for each available charge type
  const offers = availableChargeTypes.map((ct) => {
    const totalPrice = ct.price!.amount
    const currency = ct.price!.currency

    // Arrival taxes
    const arrivalTaxComponents = (ct.arrivalTaxes || []).map((tax) => ({
      '@type': 'UnitPriceSpecification',
      name: tax.name || 'GenericTax',
      price: tax.amount.amount,
      priceCurrency: tax.amount.currency,
      priceComponentType: 'GenericTax',
    }))

    // Exclusive (non-inclusive) taxes
    const exclusiveTaxComponents = (ct.taxes || [])
      .filter((tax) => !tax.inclusive)
      .map((tax) => ({
        '@type': 'UnitPriceSpecification',
        name: tax.type || 'GenericTax',
        price: tax.amount.amount,
        priceCurrency: tax.amount.currency,
        priceComponentType: 'GenericTax',
      }))

    // Base rate = total minus all tax components
    const taxTotal =
      arrivalTaxComponents.reduce((sum, t) => sum + t.price, 0) +
      exclusiveTaxComponents.reduce((sum, t) => sum + t.price, 0)
    const baseRate = Math.max(
      0,
      Math.round((totalPrice - taxTotal) * 100) / 100,
    )

    // Only include breakdown if base rate is positive (skip if tax data is inconsistent)
    const priceComponent: Record<string, unknown>[] =
      baseRate > 0
        ? [
            {
              '@type': 'UnitPriceSpecification',
              name: 'Base Rate',
              price: baseRate,
              priceCurrency: currency,
            },
            ...arrivalTaxComponents,
            ...exclusiveTaxComponents,
          ]
        : []

    // Per-pitchtype times (auto-resolve BYO vs onsite), campsite-level fallback
    const pt = pitchtypeMap.get(String(ct.pitchtypeId))
    const checkinTime = pt?.arrivalFromIso ?? fallbackCheckin
    const checkoutTime = pt?.departFromIso ?? fallbackCheckout

    const offer: Record<string, unknown> = {
      '@type': ['Offer', 'LodgingReservation'],
      ...(pt?.name && { name: pt.name }),
      checkinTime: `${input.dateRange.start} ${checkinTime}`,
      checkoutTime: `${input.dateRange.end} ${checkoutTime}`,
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'CompoundPriceSpecification',
        price: totalPrice,
        priceCurrency: currency,
        ...(priceComponent.length > 0 && { priceComponent }),
      },
    }

    return offer
  })

  // Single offer → object, multiple → array (per Google spec examples)
  schema.makesOffer = offers.length === 1 ? offers[0] : offers

  return schema
}
